import { useMemo } from 'react';
import { Layer, Map, Source } from 'react-map-gl/maplibre';
import type { LngLatBoundsLike } from 'maplibre-gl';
import type { Feature, FeatureCollection, Geometry, Point, Polygon } from 'geojson';
import { basemapStyle, mapLib } from '@/shared/lib/map/maplibre';
import { useThemeColours } from '@/shared/lib/theme/use-theme-colours';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import type { MissionSectorDto } from '@/shared/api/generated/model';
import type { LiveTrack } from '@/features/missions/model/use-mission-tracks';

/** The tokens this map paints with. Names, not values — a hex here would be a defect (§5.7). */
const TOKENS = [
  'background',
  'surfaceElevated',
  'info',
  'mutedForeground',
  'foreground',
  'coverageFill',
  'gapFill',
  'primary',
  'secondary',
] as const;

interface MissionMapProps {
  /** The search area's outline, as the server sends it: `[longitude, latitude]` pairs. */
  area: number[][];

  /** Swept ground, a GeoJSON geometry object as a string, or null when nothing has been walked. */
  coverageGeoJson?: string | null;

  /** Unswept ground, same shape. */
  gapGeoJson?: string | null;

  /**
   * The sectors still held, each with the label the radio uses.
   *
   * ⚠️ §5.9 asks this map to show which ground is assigned to whom **and which is unassigned** —
   * so what is NOT covered by one of these outlines is as much of the answer as what is.
   */
  sectors?: MissionSectorDto[];

  /**
   * Where each volunteer last reported being (§5.9).
   *
   * ⚠️ From MQTT, not from a poll — see `useMissionTracks`. Drawn last of everything, because a
   * person's position is the one thing on this map that must never be underneath anything else.
   */
  tracks?: Record<string, LiveTrack>;
}

/**
 * Where the team has walked, and where it has not (§9, §5.9's control-room view).
 *
 * ⚠️ **The gaps are the point of this screen.** Coverage is reassuring and gaps are actionable: the
 * question an operator asks at three in the morning is not "how much have we done" but "where has
 * nobody been". §9 refuses to answer the first with a percentage for that reason, and this map
 * answers the second by drawing it.
 *
 * ⚠️ Both geometries are computed **server-side by PostGIS** and delivered as GeoJSON. Recomputing
 * them in the browser from track points would give the operator and the rescuer two different
 * pictures of the same ground.
 */
export function MissionMap({
  area,
  coverageGeoJson,
  gapGeoJson,
  sectors = [],
  tracks = {},
}: MissionMapProps) {
  const { t } = useLocale();
  const colours = useThemeColours(TOKENS);

  const bounds = useMemo(() => boundsOf(area), [area]);

  const outline = useMemo<Feature<Polygon>>(
    () => ({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [closeRing(area)] },
    }),
    [area],
  );

  const sectorFeatures = useMemo<FeatureCollection<Polygon>>(
    () => ({
      type: 'FeatureCollection',
      features: sectors.map((sector) => ({
        type: 'Feature',
        properties: {
          // ⚠️ The label and the holder together, because "شمال" alone answers half the question an
          // operator is asking. An unheld sector shows its label and nothing after it, which is
          // exactly the "unassigned" §5.9 wants visible.
          label: sector.memberName ? `${sector.labelAr} · ${sector.memberName}` : sector.labelAr,
        },
        geometry: { type: 'Polygon', coordinates: [closeRing(sector.area)] },
      })),
    }),
    [sectors],
  );

  const volunteers = useMemo<FeatureCollection<Point>>(
    () => ({
      type: 'FeatureCollection',
      features: Object.values(tracks).map((track) => ({
        type: 'Feature',
        properties: { label: track.fullName },
        geometry: { type: 'Point', coordinates: [track.longitude, track.latitude] },
      })),
    }),
    [tracks],
  );

  const covered = useMemo(() => parseGeometry(coverageGeoJson), [coverageGeoJson]);
  const gaps = useMemo(() => parseGeometry(gapGeoJson), [gapGeoJson]);

  if (!bounds) {
    return <p className="text-sm text-muted-foreground">{t.missions.coverageUnavailable}</p>;
  }

  return (
    <div className="h-[28rem] w-full overflow-hidden rounded-md border border-border">
      <Map
        // ⚠️ §5.9: the panel's single MapLibre instance, handed over explicitly. Without this
        // `react-map-gl` loads its own copy and anything registered on ours is never invoked.
        mapLib={mapLib}
        initialViewState={{ bounds, fitBoundsOptions: { padding: 32 } }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={basemapStyle({
          ground: colours.background,
          earth: colours.surfaceElevated,
          wadi: colours.info,
          road: colours.mutedForeground,
          text: colours.foreground,
        })}
        // Nothing here is clickable yet, and a cursor that suggests otherwise is a small lie.
        interactiveLayerIds={[]}
      >
        {/* Order matters: the outline is drawn over the fills so a boundary is never buried. */}
        {gaps && (
          <Source id="mission-gaps" type="geojson" data={gaps}>
            <Layer
              id="mission-gaps-fill"
              type="fill"
              // ⚠️ Opacity 1: `gapFill` and `coverageFill` exist in the theme precisely for this and
              // already carry their own alpha (§7). Dimming them again here would fight the theme
              // and put the real value of the overlay somewhere no palette editor can see it.
              paint={{ 'fill-color': colours.gapFill, 'fill-opacity': 1 }}
            />
          </Source>
        )}

        {covered && (
          <Source id="mission-covered" type="geojson" data={covered}>
            <Layer
              id="mission-covered-fill"
              type="fill"
              paint={{ 'fill-color': colours.coverageFill, 'fill-opacity': 1 }}
            />
          </Source>
        )}

        {/* ⚠️ Over the fills and under the mission outline: a sector edge must never be buried by
            the ground somebody has covered, and the mission's own boundary is the outermost line on
            the screen. */}
        {sectorFeatures.features.length > 0 && (
          <Source id="mission-sectors" type="geojson" data={sectorFeatures}>
            <Layer
              id="mission-sectors-line"
              type="line"
              paint={{ 'line-color': colours.secondary, 'line-width': 1.5, 'line-dasharray': [3, 2] }}
            />
            <Layer
              id="mission-sectors-label"
              type="symbol"
              layout={{
                'text-field': ['get', 'label'],
                'text-font': ['Noto Sans Regular'],
                'text-size': 11,
              }}
              paint={{
                'text-color': colours.secondary,
                'text-halo-color': colours.background,
                'text-halo-width': 1.6,
              }}
            />
          </Source>
        )}

        <Source id="mission-area" type="geojson" data={outline}>
          <Layer
            id="mission-area-line"
            type="line"
            paint={{ 'line-color': colours.primary, 'line-width': 2 }}
          />
        </Source>
        {/* ⚠️ Last, over everything. A volunteer's position is the one thing on this map that must
            never be drawn underneath a fill, a boundary or a label. */}
        {volunteers.features.length > 0 && (
          <Source id="mission-volunteers" type="geojson" data={volunteers}>
            <Layer
              id="mission-volunteers-halo"
              type="circle"
              paint={{
                'circle-radius': 9,
                'circle-color': colours.primary,
                'circle-opacity': 0.25,
              }}
            />
            <Layer
              id="mission-volunteers-dot"
              type="circle"
              paint={{
                'circle-radius': 5,
                'circle-color': colours.primary,
                'circle-stroke-width': 2,
                'circle-stroke-color': colours.background,
              }}
            />
            <Layer
              id="mission-volunteers-label"
              type="symbol"
              layout={{
                'text-field': ['get', 'label'],
                'text-font': ['Noto Sans Regular'],
                'text-size': 11,
                'text-offset': [0, 1.2],
                'text-anchor': 'top',
              }}
              paint={{
                'text-color': colours.foreground,
                'text-halo-color': colours.background,
                'text-halo-width': 1.8,
              }}
            />
          </Source>
        )}
      </Map>
    </div>
  );
}

/**
 * The GeoJSON geometry inside a string, or null.
 *
 * ⚠️ Never throws. This runs on whatever the server sent, and a malformed geometry must cost the
 * operator one overlay — not the whole screen they are running a search from.
 */
function parseGeometry(raw?: string | null): Geometry | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    return typeof parsed === 'object' && parsed !== null && 'type' in parsed
      ? (parsed as Geometry)
      : null;
  } catch {
    return null;
  }
}

/** A ring the way GeoJSON wants it: first point repeated last. */
function closeRing(ring: number[][]): number[][] {
  const first = ring[0];
  const last = ring[ring.length - 1];

  if (!first || !last) {
    return ring;
  }

  return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
}

/** The extent of the ring, or null when there is nothing to frame. */
function boundsOf(ring: number[][]): LngLatBoundsLike | null {
  const points = ring.filter(
    (point): point is [number, number] =>
      point.length >= 2 && Number.isFinite(point[0]) && Number.isFinite(point[1]),
  );

  if (points.length === 0) {
    return null;
  }

  const longitudes = points.map(([longitude]) => longitude);
  const latitudes = points.map(([, latitude]) => latitude);

  return [
    [Math.min(...longitudes), Math.min(...latitudes)],
    [Math.max(...longitudes), Math.max(...latitudes)],
  ];
}
